import * as s from "../TableItemStyled";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "react-query";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import SearchInput from "components/atoms/input/search/SearchInput";

import AccountSelects from "../AccountSelects";
import { DashboardChartDetailTable } from "components/atoms/table/DashboardChartDetailTable";
import { TableColumnsType } from "antd";
import { postStatCvv, postWidgetCvvDetection } from "api/widgetAPI";
import {
  GetWidgetCvvStatsResponse,
  PostWidgetCvvDetectionResponse,
  WidgetCvvDetection,
  WidgetStats,
} from "api/interfaces/widgetInterface.interface";
import { getRecorderTimeFormat } from "utils/timeUtil";
import { WidgetChart } from "../TypeSelects";
import TypeSelects from "../TypeSelects";
import TableItemHeader from "../TableItemHeader";
import { Layout } from "react-grid-layout";
import NoDataChart from "components/templates/nopage/NoDataChart";
import { mobileMax } from "components/MediaQuery";
import { useMediaQuery } from "react-responsive";
import { cvvEvent } from "pages/reports/view/cvv/constants";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import useApiError from "hook/useApiError";
import { useInfiniteQuery } from "react-query";
import { commonPaginationPerPage, TableItemName } from "utils/WidgetUtil";
import { commonOrgColumn } from "pages/dashboard/dashboard/WidgetContainer";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import OrgStackedBarChart from "pages/dashboard/stats/graph/BarGraph";
import { COLORS } from "styles/colors";
import Spinner from "components/atoms/loader/Spinner";
interface Props {
  layout: Layout;
  chartLayout: Layout[];
  onDeleteWidget: (key: TableItemName) => void;
  onLayoutChange: (layout: Layout[]) => void;
}

export default function CvvDetection(props: Props): JSX.Element {
  const isMobile = useMediaQuery({ maxWidth: mobileMax });

  const [width, setWidth] = useState<number>(0);
  const chartRef = useRef<HTMLDivElement>(null);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { notifyError, lang } = useApiError();

  const [type, setType] = useState<WidgetChart>("graph");

  const [accountIds, setAccountIds] = useState<string[]>([]);
  const initialQueryInfo = {
    ...baseQueryInfo,
    pageLimit: commonPaginationPerPage,
    sortType: "createdAt",
    sortDirection: SORT_DIRECTION.DESC,
  };
  const [queryInfo, setQueryInfo] = useState<BasePagination>({
    ...initialQueryInfo,
  });
  const cvvDetectionQueryKey = useQueryKey(queryInfo);

  const columns: TableColumnsType<WidgetCvvDetection> = [
    commonOrgColumn(selectedAccount),

    {
      title: "Recorder",
      dataIndex: "recorderName",
    },

    {
      title: "Camera",
      dataIndex: "cameraName",
      render: (value, record) => `${record.cameraName}`,
    },
    {
      title: "Detection",
      render: (value, row) => {
        return cvvEvent.map((evt, index) => {
          const eventIndex = row.events.findIndex(
            (event) => event.type === evt && event.isDetect
          );
          if (eventIndex !== -1) {
            return (
              <div
                className="red"
                key={index}
                style={{ textTransform: "capitalize" }}
              >
                {evt}
              </div>
            );
          } else return null;
        });
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (value, row) =>
        value !== undefined ? (
          <s.DateWrapper>
            {getRecorderTimeFormat(value, selectedAccount, row.timezone, true)
              .split(" ")
              .map((date, index) => (
                <span key={index}>{date}</span>
              ))}
          </s.DateWrapper>
        ) : (
          ""
        ),
      hidden: isMobile,
    },
    // {
    //   title: "",
    //   render: (value, row) => <UnderlineText fontSize={14}>View</UnderlineText>,
    //   align: "right",
    //   fixed: "right",
    // },
  ];
  useEffect(() => {
    setType(
      !(selectedAccount.isHidden || selectedAccount.isAdminGroup)
        ? "list"
        : "graph"
    );
  }, [selectedAccount.accountLevel]);

  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError: isCvvDetectionError,
    refetch: refetchCvvDetection,
    isFetching,
  } = useInfiniteQuery(
    [
      "postWidgetCvvDetection",
      accountIds,
      cvvDetectionQueryKey,
      selectedAccount.accountId,
      type,
    ],
    ({ pageParam = 0 }) =>
      postWidgetCvvDetection({
        accountId: selectedAccount.accountId,
        payload: {
          accountIds,
          type: "",
          listDto: {
            ...queryInfo,
            pageNum: pageParam,
          },
        },
      }),
    {
      getNextPageParam: (
        lastPage: PostWidgetCvvDetectionResponse | undefined
      ) => {
        if (!lastPage || !lastPage.page) {
          return undefined;
        }
        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;
        return (pageNum + 1) * pageLimit < total ? nextPage : undefined;
      },
      refetchOnWindowFocus: false,
      retry: 0,
      onError: (e: any) => {},
    }
  );
  const lists = useMemo(() => {
    return (data?.pages || [])
      .flatMap((page: PostWidgetCvvDetectionResponse) => page.result ?? [])
      .map((item: WidgetCvvDetection, index: number) => ({
        ...item,
        key: index,
      }));
  }, [data]);
  const getCvvStats = useQuery(
    ["postStatCvv", accountIds, selectedAccount.accountId, type],
    () =>
      postStatCvv({
        accountId: selectedAccount.accountId,
        interval: "daily",
        payload: {
          accountIds,
          keyword: "",
        },
      }),
    {
      enabled: type === "graph",
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: GetWidgetCvvStatsResponse) => {},
      onError: (err: any) => {
        notifyError(lang.label.report.notify.cvv.list.error);
      },
    }
  );
  const graphData = useMemo(() => {
    return getCvvStats.data?.result?.stats ?? [];
  }, [getCvvStats.data?.result?.stats]);
  const refecth = useCallback(() => {
    refetchCvvDetection();
    getCvvStats.refetch();
  }, []);
  useStompDebouncedRefetch({
    stompKey: "reportListUpdated" as StompDateSignalKey,
    refetch: refecth,
  });

  const onChangeAccount = (value: string[]) => {
    setQueryInfo((quer) => {
      return {
        ...quer,
        pageNum: 0,
      };
    });
    setAccountIds(value);
  };

  const onChangeType = (value: WidgetChart) => {
    setType(value);
    setQueryInfo({
      ...initialQueryInfo,
    });
  };

  const onClickSearch = (keyword: string) => {
    setQueryInfo((quer) => {
      return {
        ...initialQueryInfo,
        keyword,
      };
    });
  };

  useEffect(() => {
    if (props.layout.h >= 2) {
      setQueryInfo((info) => {
        return {
          ...info,
          pageLimit: commonPaginationPerPage * props.layout.h,
        };
      });
    } else {
      setQueryInfo((info) => {
        return {
          ...info,
          pageLimit: commonPaginationPerPage,
        };
      });
    }
  }, [props.layout.h]);

  const isError =
    (type === "list" && isCvvDetectionError) ||
    (type === "graph" && getCvvStats.isError);
  useLayoutEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    setWidth(el.clientWidth);

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0;
      setWidth(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  return (
    <>
      <TableItemHeader
        layout={props.layout}
        chartLayout={props.chartLayout}
        onDeleteWidget={props.onDeleteWidget}
        onLayoutChange={props.onLayoutChange}
        isError={isError}
      />
      <s.Contents className="drag-cancel-content">
        <s.ContentWrapper>
          <s.ContentHeader>
            <div className="cvv-select-wrapper">
              <AccountSelects onChange={onChangeAccount} value={accountIds} />
              {(selectedAccount.isHidden || selectedAccount.isAdminGroup) && (
                <TypeSelects onChange={onChangeType} value={type} />
              )}
            </div>
            {type === "list" && (
              <div className="dashboard-search-input">
                <SearchInput onClickSearch={onClickSearch} />
              </div>
            )}
          </s.ContentHeader>
          <s.ContentInner>
            {type === "list" && (
              <DashboardChartDetailTable
                data={lists}
                loadMore={() => fetchNextPage()}
                isLoading={isInitialLoading || isFetchingNextPage || isFetching}
                hasMore={
                  hasNextPage &&
                  !isFetchingNextPage &&
                  !isInitialLoading &&
                  !isFetching
                }
                columns={columns}
              />
            )}
            {type === "graph" && (
              <div className="bar-chart-wrapper" ref={chartRef}>
                {getCvvStats.isLoading ? (
                  <Spinner />
                ) : graphData.length === 0 ||
                  graphData.every((item: WidgetStats) => item.count === 0) ? (
                  <NoDataChart />
                ) : (
                  <OrgStackedBarChart
                    rows={graphData}
                    activeOrgs={["count"]}
                    width={width}
                    items={[
                      {
                        key: "count",
                        label: "Detection",
                        color: COLORS.VALIDATERED,
                      },
                    ]}
                    groupMode={"stacked"}
                    enableGridY
                  />
                )}
              </div>
            )}
          </s.ContentInner>
        </s.ContentWrapper>
      </s.Contents>
    </>
  );
}
